import type { SelectHTMLAttributes, ReactNode } from 'react'

interface SelectOption {
  value: string
  label: string
}

interface SelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string
  error?: string
  options?: SelectOption[]
  placeholder?: string
  children?: ReactNode
}

export default function Select({
  label,
  error,
  options,
  placeholder,
  className = '',
  id,
  children,
  ...props
}: SelectProps) {
  const selectId = id || props.name

  return (
    <div className="w-full">
      {label && (
        <label htmlFor={selectId} className="block text-sm font-medium text-brand-muted mb-2">
          {label}
          {props.required && <span className="text-accent-teal ml-1">*</span>}
        </label>
      )}
      <div className="relative">
        <select
          id={selectId}
          className={`
            w-full px-[18px] py-[14px] pr-[44px]
            bg-white border border-slate-300 rounded-lg
            text-slate-900
            focus:outline-none focus:border-accent-teal focus:ring-[3px] focus:ring-accent-teal/10
            transition-all duration-300 ease-out
            appearance-none cursor-pointer
            hover:border-slate-400
            ${error ? 'border-red-500' : ''}
            ${className}
          `}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {children
            ? children
            : options?.map((option) => (
                <option key={option.value} value={option.value} className="bg-white">
                  {option.label}
                </option>
              ))}
        </select>
        {/* Custom chevron */}
        <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
          <svg
            className="w-4 h-4 text-accent-teal"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>
      {error && <p className="mt-1.5 text-sm text-red-500">{error}</p>}
    </div>
  )
}
